import { Button, Text, useBoolean, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as UpgradeIcon } from 'assets/icons/crown_upgrade_fill.svg';
import { ReactComponent as NewTabIcon } from 'assets/icons/plusSquareOutline.svg';
import BrandKitImage from 'assets/images/brand_kit.png';
import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { AutoBrand } from 'modules/Editor/components/AutoBrand';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { UserTypes } from 'modules/User';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { AddBrandModal } from './AddBrandModal';

interface AddBrandKitButtonProps {
  hasEditPermission: boolean;
  ariaLabel?: string;
}
export const AddBrandKitButton = ({ hasEditPermission, ariaLabel = '' }: AddBrandKitButtonProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });
  const [showAutoBrand, toggleAutoBrand] = useBoolean(false);
  const [showNewBrand, toggleNewBrand] = useBoolean(false);
  const { trackChameleon } = useChameleon();
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  const clickUpgradeHandler = (featureProp: string) => {
    // Set mixpanel_feature session storage item used by `Analytics: Upgrade` event
    setAnalyticsUpgradeFeatureProp(featureProp);
    Mixpanel.track(UPGRADE.eventName, {
      Feature: featureProp,
    });
    trackChameleon(UPGRADE.eventName);
    window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
  };

  const onClickUpgradeModal = () => {
    onOpenUpgradeModal();
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.Brandkit,
      plan: UserTypes.Business,
    });
  };

  const onClick = () => {
    if (!hasEditPermission) {
      onClickUpgradeModal();
      return;
    }
    toggleAutoBrand.on();
  };

  const onClickSetupManually = (closeModal: () => void) => {
    closeModal();
    toggleNewBrand.on();
  };

  return (
    <>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.Brandkit)}
        upgradePlan={UserTypes.Business}
        modalImg={BrandKitImage}
        modalType={ModalUpgradeTypeEnum.BrandKit}
      />
      <AutoBrand
        isOpen={showAutoBrand}
        onClose={toggleAutoBrand.off}
        createNew
        onClickSetupManually={onClickSetupManually}
      />

      <AddBrandModal isOpen={showNewBrand} onClose={toggleNewBrand.off} />

      <Button
        leftIcon={
          hasEditPermission ? (
            <NewTabIcon fill='var(--vg-colors-upgrade-blue-500)' />
          ) : (
            <UpgradeIcon data-testid={'gated-add-button'} />
          )
        }
        size='sm'
        variant='upgrade-outline'
        aria-label={ariaLabel}
        onClick={onClick}
        data-testid='add-new-brand-kit'
        title={t('add')}
      >
        <Text as='div' textOverflow='ellipsis' whiteSpace='nowrap' overflow='clip'>
          {t('add')}
        </Text>
      </Button>
    </>
  );
};
